import { useEffect, useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/format/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CategoryMultiSelect } from "@/features/categories/category-multi-select";
import { SerialHistoryModal } from "./serial-history-modal";

interface SerialRow {
  id: string;
  serialNo: string;
  status: string;
  variantSku: string | null;
  productName: string | null;
  currentWarehouseName?: string | null;
  importedAt?: string;
  warrantyStart: string | null;
  warrantyEnd: string | null;
}

interface Supplier {
  id: string;
  name: string;
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Nhập về",
  in_stock: "Tồn kho",
  sold: "Đã bán",
  in_repair: "Đang sửa",
  rma_to_vendor: "RMA nhà cung cấp",
  defective: "Hỏng",
  returned: "Hoàn trả",
};

const STATUS_COLORS: Record<string, string> = {
  in_stock: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300",
  sold: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  in_repair: "bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300",
  rma_to_vendor: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  defective: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  returned: "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300",
  imported: "bg-muted text-muted-foreground",
};

// Only the statuses worth filtering by, in a sensible order.
const STATUS_FILTER_OPTIONS = [
  { value: "in_stock", label: "Tồn kho" },
  { value: "sold", label: "Đã bán" },
  { value: "in_repair", label: "Đang sửa" },
  { value: "rma_to_vendor", label: "RMA nhà cung cấp" },
  { value: "defective", label: "Hỏng" },
  { value: "returned", label: "Hoàn trả" },
];

const WARRANTY_OPTIONS = [
  { value: "valid", label: "Còn bảo hành" },
  { value: "expired", label: "Hết bảo hành" },
  { value: "none", label: "Không có bảo hành" },
];

/** Debounce a rapidly-changing value so typing doesn't fire a request per keystroke. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function warrantyCell(s: SerialRow) {
  if (!s.warrantyEnd) return <span className="text-muted-foreground">—</span>;
  const expired = new Date(s.warrantyEnd) < new Date();
  return (
    <div className="text-sm">
      <span className={expired ? "text-destructive font-medium" : "font-medium"}>{formatDate(s.warrantyEnd)}</span>
      {expired && <span className="ml-1 text-xs text-destructive">(hết hạn)</span>}
      <div className="text-xs text-muted-foreground">từ {formatDate(s.warrantyStart)}</div>
    </div>
  );
}

export function SerialLookupPage() {
  const [q, setQ] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [product, setProduct] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [status, setStatus] = useState("");
  const [warranty, setWarranty] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [historySerial, setHistorySerial] = useState<string | null>(null);

  const dq = useDebounced(q);
  const dProduct = useDebounced(product);

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => apiClient.get<{ data: Supplier[] }>("/v1/suppliers").then((r) => r.data.data),
  });

  const hasAdvanced = !!(
    categoryIds.length || dProduct || supplierId || status || warranty || dateFrom || dateTo
  );

  // Tải theo trang 500 + nút "Tải thêm" — chị Thủy cần xem HẾT IMEI của một mã
  // hàng, không dừng ở 200 như trước (20/08). BE trả meta.total để biết còn không.
  const PAGE_SIZE = 500;
  const params: Record<string, string | number> = { limit: PAGE_SIZE };
  if (dq.trim()) params.q = dq.trim();
  if (categoryIds.length) params.category_ids = categoryIds.join(",");
  if (dProduct.trim()) params.product = dProduct.trim();
  if (supplierId) params.supplier_id = supplierId;
  if (status) params.status = status;
  if (warranty) params.warranty = warranty;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const {
    data: pages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["serials", params],
    queryFn: ({ pageParam }) =>
      apiClient
        .get<{ data: SerialRow[]; meta?: { total?: number } }>("/v1/serials", {
          params: { ...params, offset: pageParam },
        })
        .then((r) => r.data),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((sum, p) => sum + p.data.length, 0);
      const total = last.meta?.total ?? 0;
      // Trang cuối trả thiếu (không đủ PAGE_SIZE) cũng là hết dữ liệu.
      if (last.data.length < PAGE_SIZE) return undefined;
      return loaded < total ? loaded : undefined;
    },
  });

  const serials = pages?.pages.flatMap((p) => p.data) ?? [];
  const total = pages?.pages[0]?.meta?.total ?? serials.length;

  function clearAll() {
    setQ("");
    setCategoryIds([]);
    setProduct("");
    setSupplierId("");
    setStatus("");
    setWarranty("");
    setDateFrom("");
    setDateTo("");
  }

  // Xuất Excel TOÀN BỘ theo bộ lọc hiện tại — tải hết các trang rồi ghép CSV
  // (BOM UTF-8, mở thẳng bằng Excel; FE chưa có lib xlsx).
  async function exportAll() {
    if (exporting) return;
    setExporting(true);
    try {
      const all: SerialRow[] = [];
      let offset = 0;
      for (;;) {
        const res = await apiClient.get<{ data: SerialRow[]; meta?: { total?: number } }>(
          "/v1/serials",
          { params: { ...params, offset } },
        );
        all.push(...res.data.data);
        const totalCount = res.data.meta?.total ?? all.length;
        if (res.data.data.length < PAGE_SIZE || all.length >= totalCount) break;
        offset = all.length;
      }
      const head = ["IMEI / Serial", "SKU", "Tên sản phẩm", "Trạng thái", "Kho", "Ngày nhập", "BH từ", "BH đến"];
      const lines = all.map((s) => [
        s.serialNo,
        s.variantSku ?? "",
        s.productName ?? "",
        STATUS_LABELS[s.status] ?? s.status,
        s.currentWarehouseName ?? "",
        s.importedAt ? formatDate(s.importedAt) : "",
        s.warrantyStart ? formatDate(s.warrantyStart) : "",
        s.warrantyEnd ? formatDate(s.warrantyEnd) : "",
      ]);
      const csv = [head, ...lines]
        .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tra-cuu-imei.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Tra cứu IMEI</h1>

      {/* Basic search + advanced toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1 max-w-md">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo IMEI / serial, mã SKU hoặc tên sản phẩm…"
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <Button
          variant={showAdvanced ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAdvanced((v) => !v)}
          className="gap-1.5"
        >
          <SlidersHorizontal size={15} />
          Tìm nâng cao
          {hasAdvanced && <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-xs">•</span>}
        </Button>
        {(q || hasAdvanced) && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1 text-muted-foreground">
            <X size={14} /> Xóa lọc
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={exporting || total === 0}
          onClick={() => void exportAll()}
          title="Xuất toàn bộ IMEI theo bộ lọc hiện tại ra file mở bằng Excel"
        >
          {exporting ? "Đang xuất…" : `Xuất Excel (${total})`}
        </Button>
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhóm sản phẩm</span>
            <CategoryMultiSelect values={categoryIds} onChange={setCategoryIds} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Tên sản phẩm (tên / SKU)</span>
            <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Tên sản phẩm hoặc SKU…" />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhà cung cấp</span>
            <SearchableSelect
              value={supplierId}
              onChange={setSupplierId}
              options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              placeholder="Tất cả nhà cung cấp"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Trạng thái bán</span>
            <SearchableSelect
              value={status}
              onChange={setStatus}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Tất cả trạng thái"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Bảo hành</span>
            <SearchableSelect
              value={warranty}
              onChange={setWarranty}
              options={WARRANTY_OPTIONS}
              placeholder="Tất cả"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhập từ ngày</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Nhập đến ngày</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>IMEI / Serial</Th>
              <Th>SKU</Th>
              <Th>Tên sản phẩm</Th>
              <Th>Trạng thái</Th>
              <Th>Bảo hành đến</Th>
            </Tr>
          </THead>
          <TBody>
            {serials.length === 0 && (
              <Tr>
                <Td colSpan={5} className="text-center text-muted-foreground">
                  {q.trim() || hasAdvanced ? "Không tìm thấy kết quả" : "Chưa có IMEI / serial nào"}
                </Td>
              </Tr>
            )}
            {serials.map((s) => (
              <Tr key={s.id} className="cursor-pointer" onClick={() => setHistorySerial(s.serialNo)}>
                <Td className="font-mono text-sm text-primary underline-offset-2 hover:underline">{s.serialNo}</Td>
                <Td className="font-mono text-xs">{s.variantSku ?? "—"}</Td>
                <Td>{s.productName ?? "—"}</Td>
                <Td>
                  <span
                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[s.status] ?? "bg-muted text-muted-foreground"}`}
                  >
                    {STATUS_LABELS[s.status] ?? s.status}
                  </span>
                </Td>
                <Td>{warrantyCell(s)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {!isLoading && serials.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Đang hiện <b className="tabular-nums">{serials.length}</b> /{" "}
            <b className="tabular-nums">{total}</b> IMEI
          </p>
          {hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? "Đang tải…" : `Tải thêm ${Math.min(PAGE_SIZE, total - serials.length)}`}
            </Button>
          )}
        </div>
      )}

      {historySerial && (
        <SerialHistoryModal serialNo={historySerial} onClose={() => setHistorySerial(null)} />
      )}
    </div>
  );
}
